import PageShell from "@/components/PageShell";
import PageMeta from "@/components/PageMeta";

const Section = ({ id, title, children }) => (
  <section className="mb-8" id={id}>
    <h2 className="text-xl md:text-2xl tracking-tight mb-3" data-testid={`terms-${id}`}>{title}</h2>
    <div className="text-black/75 leading-relaxed space-y-3 text-[15px]">{children}</div>
  </section>
);

export default function Terms() {
  const updated = "February 18, 2026";
  return (
    <PageShell
      eyebrow="Legal"
      title="Terms of Service."
      subtitle={`Last updated: ${updated}. By using SendMe you agree to these terms. Please read them — they're written in plain English on purpose.`}
      narrow
    >
      <PageMeta
        title="Terms of Service"
        description="SendMe Terms of Service — acceptable use, subscriptions, refunds, liability. Plain English, 15 sections. Last updated February 2026."
      />
      <div className="max-w-3xl">
        <Section id="acceptance" title="1. Acceptance of these terms">
          <p>
            Welcome to SendMe ("we", "us", "our"). These Terms of Service ("Terms") form a binding agreement between you ("you", "user") and the operator of <strong>sendme.co.in</strong>. When you upload, download, share, or otherwise use any feature of SendMe — including transferring files, joining a team workspace, or subscribing to a paid plan — you confirm that you have read, understood, and accepted these Terms.
          </p>
          <p>
            If you do not agree with any part of these Terms, please do not use the service. We may update these Terms from time to time. We'll update the "Last updated" date at the top, and where the change is material we'll surface a notice in the app so you have a fair opportunity to review before continuing to use SendMe.
          </p>
        </Section>

        <Section id="eligibility" title="2. Who can use SendMe">
          <p>
            You must be at least 13 years old to use the anonymous (no-signup) features, and at least 18 years old — or the age of majority in your jurisdiction, whichever is greater — to register an account, subscribe to a paid plan, or create a team workspace. If you're using SendMe on behalf of an employer or organization, you confirm that you have authority to bind that entity to these Terms.
          </p>
          <p>
            SendMe is offered globally, but we may restrict access from regions where local law makes operating the service impractical. We don't intentionally collect data from children under 13 and will delete any such account on notice.
          </p>
        </Section>

        <Section id="acceptable-use" title="3. Acceptable use">
          <p>SendMe is a tool for moving lawful content between people. You agree <strong>not</strong> to use the service to:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Distribute malware, viruses, ransomware, or any code intended to disrupt or damage hardware, software, or systems.</li>
            <li>Share content that infringes copyright, trademark, trade secret, or any other intellectual-property right.</li>
            <li>Transmit child sexual abuse material (CSAM), non-consensual intimate imagery, or content that incites violence or self-harm.</li>
            <li>Harass, dox, defame, or impersonate another person.</li>
            <li>Send commercial spam, phishing payloads, or unsolicited mass communications.</li>
            <li>Reverse-engineer, scrape, or attempt to extract source code or data from SendMe other than your own transfers.</li>
            <li>Circumvent rate limits, expiry windows, or any other technical control built into the service.</li>
          </ul>
          <p>
            We reserve the right to remove any content that violates these rules, suspend or terminate offending accounts, and cooperate with law enforcement where legally required. We also reserve the right (but undertake no obligation) to refuse service to anyone for any lawful reason.
          </p>
        </Section>

        <Section id="content" title="4. Your content">
          <p>
            You retain all ownership of the files you upload. By uploading, you grant SendMe a limited, worldwide, non-exclusive, royalty-free licence to host, store, transmit, and serve your files solely for the purpose of providing the service to you and your designated recipients. This licence terminates the moment the transfer expires or you delete it — whichever comes first.
          </p>
          <p>
            We do not browse, index, or commercially exploit your files. We do not train AI models on user content. We do not sell your data.
          </p>
        </Section>

        <Section id="expiry" title="5. Expiry, storage and deletion">
          <p>
            All transfers expire automatically. The default expiry is 24 hours; paid plans can extend up to 7 days. After expiry, files are removed from active storage. We may retain anonymised metadata (size, content-type, expiry duration) for analytics and abuse-prevention purposes, but the file content itself is purged.
          </p>
          <p>
            You can delete any transfer earlier from your dashboard. Deletion is immediate and irreversible — please keep a local copy if you might need the file again.
          </p>
        </Section>

        <Section id="paid-plans" title="6. Paid plans, billing and refunds">
          <p>
            SendMe Pro is billed monthly at ₹699 (or local equivalent) via Razorpay. Subscriptions auto-renew until cancelled. You can cancel anytime from your profile; cancellation takes effect at the end of the current billing period and you retain Pro features until then.
          </p>
          <p>
            We offer a no-questions refund within 7 days of your first payment. After that, refunds are at our discretion and typically granted only for service outages exceeding 24 hours. Refunds are returned via the original payment method.
          </p>
          <p>
            Prices are inclusive of GST where applicable. Bank or card fees levied by your provider are not refundable.
          </p>
        </Section>

        <Section id="team-workspaces" title="7. Team workspaces">
          <p>
            Team workspaces allow multiple users to collaborate on a shared folder. The team creator (owner) is responsible for managing membership and is liable for the content shared within the workspace, jointly with the member who uploaded it. Owners can remove members and delete any content in the workspace at any time.
          </p>
          <p>
            If a team subscription lapses, the workspace enters read-only mode for 30 days, after which the content is permanently deleted. Members will be notified by email before deletion.
          </p>
        </Section>

        <Section id="intellectual-property" title="8. Our intellectual property">
          <p>
            The SendMe name, logo, design, software, and all related materials are our intellectual property and protected by copyright, trademark, and other laws. We grant you a personal, revocable, non-transferable, non-exclusive licence to use the service for its intended purpose. You may not redistribute, sublicense, or resell any part of SendMe.
          </p>
        </Section>

        <Section id="warranties" title="9. Disclaimer of warranties">
          <p>
            SendMe is provided <strong>"as is"</strong> and <strong>"as available"</strong>. We make commercially reasonable efforts to keep the service reliable and secure, but we cannot guarantee uninterrupted operation, freedom from bugs, or that the service will meet every requirement you have. To the maximum extent permitted by law, we disclaim all implied warranties — including merchantability, fitness for a particular purpose, and non-infringement.
          </p>
        </Section>

        <Section id="liability" title="10. Limitation of liability">
          <p>
            To the maximum extent permitted by applicable law, SendMe, its officers, employees, and partners shall not be liable for any indirect, incidental, special, consequential, or punitive damages — including loss of profits, data, goodwill, or other intangibles — arising out of or related to your use of the service.
          </p>
          <p>
            Our aggregate liability for any direct damages shall not exceed the greater of (a) the amount you paid us in the twelve months preceding the claim, or (b) ₹5,000.
          </p>
        </Section>

        <Section id="indemnity" title="11. Indemnity">
          <p>
            You agree to indemnify and hold SendMe harmless from any claim, demand, or expense (including reasonable attorneys' fees) arising out of your breach of these Terms, your misuse of the service, or your violation of any third-party right — including any intellectual-property or privacy right.
          </p>
        </Section>

        <Section id="termination" title="12. Termination">
          <p>
            You can stop using SendMe at any time. We can suspend or terminate your access if you breach these Terms, fail to pay a subscription, or cause us reasonable concern about ongoing abuse. On termination we'll delete your account and content within a reasonable period, except where retention is required by law.
          </p>
        </Section>

        <Section id="law" title="13. Governing law and disputes">
          <p>
            These Terms are governed by the laws of India. Any dispute arising from your use of SendMe shall be subject to the exclusive jurisdiction of the courts at Delhi, India. We encourage you to write to us first at <a className="underline" href="mailto:legal@sendme.co.in">legal@sendme.co.in</a> — most disagreements can be resolved without a lawyer.
          </p>
        </Section>

        <Section id="changes" title="14. Changes to these Terms">
          <p>
            We may revise these Terms periodically. The "Last updated" date at the top of this page will always reflect the most recent change. If we make a material change, we'll notify you by email (if you have an account) or by an in-app banner at least 14 days before the change takes effect.
          </p>
        </Section>

        <Section id="contact" title="15. Contact us">
          <p>
            Questions about these Terms? Write to us at <a className="underline" href="mailto:legal@sendme.co.in">legal@sendme.co.in</a> or use the contact form on <a className="underline" href="/contact">/contact</a>. We read every email and reply within two business days.
          </p>
        </Section>

        <p className="mt-12 text-xs text-black/45 font-mono">
          By continuing to use SendMe you confirm you have read and accepted these Terms.
        </p>
      </div>
    </PageShell>
  );
}
